import React, { useEffect, useState } from 'react';
import {
  collection, getDocs, query, where,
  doc, addDoc, updateDoc, getDoc, orderBy
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

// ─── Status flow ──────────────────────────────────────────────
// draft → agreed → reported → reviewed
const STATUS_LABELS = {
  draft:    { label: 'Draft',    color: '#6b6b6b', desc: 'Being prepared' },
  agreed:   { label: 'Agreed',   color: '#0369a1', desc: 'Plan locked — awaiting outcome' },
  reported: { label: 'Reported', color: '#166534', desc: 'Outcome submitted — awaiting coach review' },
  reviewed: { label: 'Reviewed', color: '#065f46', desc: 'Complete' },
};

const DEFAULT_BUCKETS = [
  { id: 'business_model',    label: 'Business Model',      icon: '🗂',  description: 'Optimising or pivoting the business model' },
  { id: 'technology',        label: 'Technology & Systems', icon: '⚙️', description: 'Developing or upgrading technology' },
  { id: 'customer_journey',  label: 'Customer Journey',     icon: '🗺',  description: 'Improving user experience and customer touchpoints' },
  { id: 'learning_outcomes', label: 'Learning Outcomes',    icon: '📈',  description: 'Validating and improving educational impact' },
];

const OUTCOME_METRICS = [
  'Number of active learners this period',
  'Course/content completion rate (%)',
  'Pre/post assessment improvement (%)',
  'Teacher or facilitator satisfaction score',
  'Evidence of learning outcome or behaviour change',
  'Learner retention rate (%)',
];

export default function WorkPlansPage({ companyId: propCompanyId, sessionId: propSessionId, isCoach, readOnly }) {
  const { profile } = useAuth();
  const [companyDocId, setCompanyDocId] = useState(propCompanyId || null);
  const [sessions, setSessions]         = useState([]);
  const [selectedSession, setSelectedSession] = useState(propSessionId || null);
  const [workPlans, setWorkPlans]       = useState({});
  const [buckets, setBuckets]           = useState(DEFAULT_BUCKETS);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(null);
  const [saved, setSaved]               = useState(null);
  const [drafts, setDrafts]             = useState({});
  const autoSaveTimers                  = React.useRef({});

  const isBeneficiary = profile?.role === 'beneficiary';
  const isCoachRole   = profile?.role === 'coach' || isCoach;

  // Load company
  useEffect(() => {
    if (propCompanyId) { setCompanyDocId(propCompanyId); return; }
    if (!profile?.uid) return;
    async function findCompany() {
      try {
        const field = profile.role === 'beneficiary' ? 'memberUids' : 'coachId';
        const op    = profile.role === 'beneficiary' ? 'array-contains' : '==';
        const q = query(collection(db, 'companies'), where(field, op, profile.uid));
        const snap = await getDocs(q);
        if (!snap.empty) setCompanyDocId(snap.docs[0].id);
      } catch (err) { console.error(err); }
    }
    findCompany();
  }, [profile, propCompanyId]);

  // Load sessions and cohort buckets
  useEffect(() => {
    if (!companyDocId) return;
    async function load() {
      try {
        const compDoc = await getDoc(doc(db, 'companies', companyDocId));
        const comp = compDoc.data();
        if (comp?.cohortId) {
          const cohortDoc = await getDoc(doc(db, 'cohorts', comp.cohortId));
          if (cohortDoc.exists() && cohortDoc.data().buckets) setBuckets(cohortDoc.data().buckets);
        }
        const sSnap = await getDocs(
          query(collection(db, 'companies', companyDocId, 'coachingSessions'), orderBy('sessionDate', 'desc'))
        );
        const sessionList = sSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setSessions(sessionList);
        if (!selectedSession && sessionList.length > 0) setSelectedSession(sessionList[0].id);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, [companyDocId, selectedSession]);

  // Load work plans for selected session
  useEffect(() => {
    if (!companyDocId || !selectedSession) return;
    async function loadPlans() {
      try {
        const snap = await getDocs(
          query(collection(db, 'companies', companyDocId, 'workPlans'), where('sessionId', '==', selectedSession))
        );
        const planMap = {};
        const draftInit = {};
        snap.docs.forEach(d => {
          const data = { id: d.id, ...d.data() };
          planMap[data.bucketId] = data;
          draftInit[data.bucketId] = { plan: data.plan||'', outcome: data.outcome||'', metrics: data.metrics||{}, scalingLink: data.scalingLink||'' };
        });
        setWorkPlans(planMap);
        setDrafts(draftInit);
      } catch (err) { console.error(err); }
    }
    loadPlans();
  }, [companyDocId, selectedSession]);

  // ── Save / auto-save ─────────────────────────────────────────
  const handleSave = async (bucketId, draftsOverride) => {
    if (!companyDocId || !selectedSession) return;
    const currentDrafts = draftsOverride || drafts;
    const existing = workPlans[bucketId];
    const status = existing?.status || 'draft';

    // Don't auto-save if fully locked
    if (status === 'reviewed') return;

    setSaving(bucketId);
    try {
      const bucket = buckets.find(b => b.id === bucketId);
      const draft  = currentDrafts[bucketId] || {};
      const data = {
        companyId:   companyDocId,
        sessionId:   selectedSession,
        bucketId,
        bucketLabel: bucket?.label || bucketId,
        plan:        draft.plan    || '',
        outcome:     draft.outcome || '',
        metrics:     draft.metrics || {},
        scalingLink: draft.scalingLink || '',
        updatedAt:   new Date(),
        updatedBy:   profile.uid,
      };

      if (existing) {
        await updateDoc(doc(db, 'companies', companyDocId, 'workPlans', existing.id), data);
        setWorkPlans(prev => ({ ...prev, [bucketId]: { ...existing, ...data } }));
      } else {
        data.createdAt = new Date();
        data.createdBy = profile.uid;
        data.status    = 'draft';
        const ref = await addDoc(collection(db, 'companies', companyDocId, 'workPlans'), data);
        setWorkPlans(prev => ({ ...prev, [bucketId]: { id: ref.id, ...data } }));
      }
      setSaved(bucketId);
      setTimeout(() => setSaved(null), 2000);
    } catch (err) { console.error(err); }
    finally { setSaving(null); }
  };

  // ── Status transitions ────────────────────────────────────────
  const transitionStatus = async (bucketId, newStatus) => {
    const existing = workPlans[bucketId];
    if (!existing) return;
    // Save latest drafts first
    await handleSave(bucketId);
    try {
      await updateDoc(doc(db, 'companies', companyDocId, 'workPlans', existing.id), {
        status: newStatus, updatedAt: new Date(), updatedBy: profile.uid
      });
      setWorkPlans(prev => ({ ...prev, [bucketId]: { ...prev[bucketId], status: newStatus } }));
    } catch (err) { console.error(err); }
  };

  // ── Auto-save trigger ─────────────────────────────────────────
  const updateDraft = (bucketId, field, value) => {
    setDrafts(prev => {
      const updated = { ...prev, [bucketId]: { ...(prev[bucketId]||{}), [field]: value } };
      const status  = workPlans[bucketId]?.status || 'draft';
      if (status !== 'reviewed') {
        if (autoSaveTimers.current[bucketId]) clearTimeout(autoSaveTimers.current[bucketId]);
        autoSaveTimers.current[bucketId] = setTimeout(() => handleSave(bucketId, updated), 2000);
      }
      return updated;
    });
  };

  const updateMetric = (bucketId, metric, value) => {
    setDrafts(prev => ({
      ...prev,
      [bucketId]: { ...(prev[bucketId]||{}), metrics: { ...(prev[bucketId]?.metrics||{}), [metric]: value } }
    }));
  };

  // ── What can be edited by whom ────────────────────────────────
  const canEditPlan    = (status) => !readOnly && (status === 'draft');
  const canEditOutcome = (status) => !readOnly && (status === 'draft' || status === 'agreed');

  if (loading) return <div className="gm-loading"><div className="gm-spinner" /></div>;

  return (
    <div className="gm-workplans">
      {/* Session selector */}
      {!propSessionId && sessions.length > 0 && (
        <div className="gm-workplans-session-selector">
          <label className="gm-workplans-session-label">Session</label>
          <select value={selectedSession||''} onChange={e => setSelectedSession(e.target.value)}>
            {sessions.map(s => {
              const date = s.sessionDate?.toDate ? s.sessionDate.toDate() : new Date(s.sessionDate);
              return (
                <option key={s.id} value={s.id}>
                  {date.toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {sessions.length === 0 && (
        <div className="gm-empty-state"><p>No coaching sessions yet. Work plans are created alongside coaching sessions.</p></div>
      )}

      {selectedSession && buckets.map(bucket => {
        const plan    = workPlans[bucket.id];
        const status  = plan?.status || 'draft';
        const statusInfo = STATUS_LABELS[status];
        const draft   = drafts[bucket.id] || {};
        const isSavingThis = saving === bucket.id;
        const isSavedThis  = saved  === bucket.id;
        const planLocked    = !canEditPlan(status);
        const outcomeLocked = !canEditOutcome(status);

        return (
          <div key={bucket.id} className={`gm-workplan-bucket ${plan ? 'gm-workplan-bucket--active' : ''}`}>
            <div className="gm-workplan-bucket-header">
              <span className="gm-workplan-bucket-icon">{bucket.icon}</span>
              <div style={{ flex: 1 }}>
                <div className="gm-workplan-bucket-title">{bucket.label}</div>
                <div className="gm-workplan-bucket-desc">{bucket.description}</div>
              </div>
              {/* Status badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <span className="gm-workplan-status" style={{ background: statusInfo.color + '22', color: statusInfo.color }}>
                  {statusInfo.label}
                </span>
                {isSavingThis && <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>saving…</span>}
                {isSavedThis  && <span className="gm-save-success" style={{ fontSize: '0.7rem' }}>✓</span>}
              </div>
            </div>

            <div className="gm-workplan-fields">
              {/* Plan */}
              <div className="gm-form-group">
                <label>
                  What will we work on? (Plan)
                  {planLocked && <span className="gm-locked-badge">🔒 Locked</span>}
                </label>
                {planLocked ? (
                  <div className="gm-workplan-locked-text">{draft.plan || <em>Not filled in</em>}</div>
                ) : (
                  <textarea rows={3}
                    placeholder={`What specific activities under ${bucket.label} before the next session?`}
                    value={draft.plan||''}
                    onChange={e => updateDraft(bucket.id, 'plan', e.target.value)}
                  />
                )}
              </div>

              {/* Outcome */}
              <div className="gm-form-group">
                <label>
                  What happened? (Outcome)
                  {outcomeLocked && <span className="gm-locked-badge">🔒 Locked</span>}
                </label>
                {outcomeLocked ? (
                  <div className="gm-workplan-locked-text">{draft.outcome || <em>Not reported</em>}</div>
                ) : (
                  <textarea rows={3}
                    placeholder="Report back: what was achieved, what wasn't, and why?"
                    value={draft.outcome||''}
                    onChange={e => updateDraft(bucket.id, 'outcome', e.target.value)}
                  />
                )}
              </div>

              {/* Learning outcome metrics */}
              {bucket.id === 'learning_outcomes' && (
                <div className="gm-workplan-metrics">
                  <div className="gm-workplan-metrics-title">Learning outcome metrics</div>
                  {(bucket.outcomeMetrics || OUTCOME_METRICS).map(metric => (
                    <div key={metric} className="gm-workplan-metric-row">
                      <label className="gm-workplan-metric-label">{metric}</label>
                      <input type="text" placeholder="Enter value"
                        value={draft.metrics?.[metric]||''}
                        onChange={e => updateMetric(bucket.id, metric, e.target.value)}
                        disabled={outcomeLocked}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Scaling link */}
              <div className="gm-form-group">
                <label>How does this translate to scaling stage movement?</label>
                {outcomeLocked ? (
                  <div className="gm-workplan-locked-text">{draft.scalingLink || <em>Not filled in</em>}</div>
                ) : (
                  <textarea rows={2}
                    placeholder="Describe how this work will move the needle on the scaling journey…"
                    value={draft.scalingLink||''}
                    onChange={e => updateDraft(bucket.id, 'scalingLink', e.target.value)}
                  />
                )}
              </div>

              {/* Status action buttons */}
              {!readOnly && (
                <div className="gm-workplan-actions">
                  {/* Coach: agree plan (draft → agreed) */}
                  {isCoachRole && status === 'draft' && draft.plan && (
                    <button className="gm-btn gm-btn-primary gm-btn-sm" onClick={() => transitionStatus(bucket.id, 'agreed')}>
                      ✓ Agree plan
                    </button>
                  )}
                  {/* Beneficiary: submit outcome (agreed → reported) */}
                  {isBeneficiary && status === 'agreed' && draft.outcome && (
                    <button className="gm-btn gm-btn-primary gm-btn-sm" onClick={() => transitionStatus(bucket.id, 'reported')}>
                      Submit outcome
                    </button>
                  )}
                  {/* Coach: mark reviewed (reported → reviewed) */}
                  {isCoachRole && status === 'reported' && (
                    <button className="gm-btn gm-btn-primary gm-btn-sm" onClick={() => transitionStatus(bucket.id, 'reviewed')}>
                      ✓ Mark reviewed
                    </button>
                  )}
                  {/* Coach: reopen if needed */}
                  {isCoachRole && (status === 'agreed' || status === 'reported') && (
                    <button className="gm-btn gm-btn-outline gm-btn-sm" onClick={() => transitionStatus(bucket.id, 'draft')}>
                      Reopen
                    </button>
                  )}
                  <span className="gm-workplan-status-desc">{statusInfo.desc}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
